import {
  getItemId,
  getName,
  getUserId,
  isMobile,
  isNurse,
} from "@/src/utils/utils";
import FullCalendar from "@fullcalendar/react";
import moment from "moment";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import timeGridPlugin from "@fullcalendar/timegrid";
import {
  FolderItem,
  MedicineUnit,
  Patient,
  Task,
  treatmentCategories,
  TreatmentCategory,
} from "@/src/data/allTypes";
import { EventSourceInput } from "@fullcalendar/core";
import { Button } from "@/src/components/buttons";
import {
  closeDialog,
  openConfirmDialog,
  openDialog,
} from "@/src/components/dialogs";
import apiFetch from "@/src/utils/apiFetch";
import { openOrdonnanceMedicineForm } from "../medicines/ordonnanceMedicineForm";
import { USER } from "@/src/data/globalVariables";
import { UniqueTaskForm } from "./uniqueTaskForm";
import { TreatmentResource } from "./treatmentResource";
import { TreatmentEvent } from "./treatmentEvent";
import { openTaskActions } from "./taskActions";

export function TreatmentTimeline({
  tasks,
  patient,
  parent,
  start,
  end,
  onChange,
}: {
  tasks: Task[];
  patient: Patient;
  parent: FolderItem;
  start: string;
  end: string;
  onChange: () => void;
}) {
  function getResources() {
    const categoriesIds: TreatmentCategory[] = [
      "Per os",
      "Injectable",
      "Aerosols",
      "Solutés",
      "Gestes",
    ];
    return categoriesIds.map((categoryId) => {
      let category = treatmentCategories[categoryId];
      return {
        id: category.id,
        title: category.name,
        children: tasks
          ?.filter((task) => {
            if (!task.start) return false;
            if (!task.medicine) return false;
            if (task.start < start || task.start > end) return false;
            task.medicine.form ||= "comprimé";
            if (
              task.unit &&
              (
                [
                  "injection",
                  "cc",
                  "cc/h",
                  "mg/h",
                  "mg/kg",
                  "mg/kg/h",
                  "mg/m2",
                  "ml",
                  "ml/h",
                  "ml/kg",
                  "ml/kg/h",
                  "ml/m2",
                  "ml/m2/h",
                ] as MedicineUnit[]
              ).includes(task.unit)
            )
              task.medicine.form = "injectable";

            return (
              task.medicine.form != null &&
              category.forms.includes(task.medicine.form)
            );
          })
          .map((task) => {
            return {
              id: task.medicine?.id || "",
              title: task.medicine?.name || "Sans nom",
            };
          }),
      };
    });
  }

  function getRemainingDays(medicineId: string) {
    let lastDate = moment(start).format("YYYY-MM-DD");
    for (let task of tasks || []) {
      if (task.medicine?.id != medicineId) continue;
      if (!task.start) continue;
      if (moment(task.start).isBefore(lastDate)) continue;
      lastDate = task.start;
    }
    return moment(lastDate).diff(moment(start), "days");
  }

  async function openEditDialog(medicineId: string) {
    await openDialog({
      id: "editActionsDialog",
      title: "Actions",
      content: (
        <div className="c1 gap10">
          <Button
            type={"tertiary"}
            onClick={async () => {
              let medicineTasks =
                tasks?.filter(
                  (task) => getItemId(task.medicine) == medicineId
                ) || [];

              let medicine = medicineTasks?.[0]?.medicine;
              if (!medicine) throw new Error("Médicament introuvable");

              let ordonnanceMedicine = await apiFetch(
                "ordonnances",
                "loadOrdonnanceMedicine",
                {
                  tasksIds: medicineTasks?.map((task) => getItemId(task) || ""),
                  medicineId,
                }
              );

              closeDialog("editActionsDialog");
              await openOrdonnanceMedicineForm({
                medicine,
                ordonnanceMedicine,
                patient,
                parent,
                onBeforeConfim: async (ordonnanceMedicine) => {
                  return await openDeleteDialog(
                    medicineId,
                    "Voulez-vous vraiment remplacer la prescription actuelle par celle-ci ? \n\n Toutes les tâches non effectuées seront supprimées."
                  );
                },
                onConfirm: (ordonnance) => {},
              });
            }}
          >
            Modifier la prescription
          </Button>
          <Button
            type={"tertiary"}
            color="red"
            onClick={async () => {
              closeDialog("editActionsDialog");
              await openDeleteDialog(medicineId);
            }}
          >
            Supprimer les prises futures
          </Button>
        </div>
      ),
    });
  }

  async function openDeleteDialog(medicineId: string, message?: string) {
    return await openConfirmDialog({
      id: "deleteTasksDialog",
      title: "Supprimer les prises",
      content:
        message ||
        "Voulez-vous vraiment supprimer toutes les tâches futures pour cet élément ?",
      onConfirm: async () => {
        const deletableTasks = tasks?.filter(
          (task) =>
            getItemId(task.medicine) == medicineId && task.status != "finished"
        );
        await apiFetch("tasks", "deleteMany", {
          tasksIds: deletableTasks?.map((task) => task.id) || [],
        });
        onChange();
      },
    });
  }

  async function onDateClick(anyTask: Task, start: string) {
    if (isNurse(USER?.get())) return;
    let medicine = anyTask?.medicine;
    let task: Task = {
      patient,
      parent,
      creator: getUserId(),
      doctor: getUserId(),
      medicine,
      start,
      notes: "",
      end: moment(start).add(1, "hour").format("YYYY-MM-DD HH:mm"),
      posology: 1,
      unit: anyTask.unit,
      route: anyTask.route,
    };

    await openDialog({
      id: "uniqueTaskDrawer",
      title: "Ajouter une prise",
      content: (
        <UniqueTaskForm
          current={task}
          onChange={(newValue) => {
            task = newValue;
          }}
        />
      ),
      onConfirm: async () => {
        console.log({ task });
        await apiFetch(
          "tasks",
          "addMany",
          { tasks: [task] },
          "Erreur lors de l'ajout de la tâche"
        );
        onChange();
      },
    });
  }

  return (
    <div className="calendar">
      <FullCalendar
        key={`FullCalendarTreatment${start}`}
        locale={"fr"}
        height={"auto"}
        resourceOrder=""
        initialDate={start || moment().format("YYYY-MM-DD HH:mm:ss")}
        headerToolbar={{
          left: "",
          center: "",
          right: "",
        }}
        // buttonText={{
        //   today: "Aujourd'hui",
        //   month: "Mois",
        //   week: "Semaine",
        //   day: "Jour",
        //   list: "Liste",
        // }}
        eventMinHeight={50}
        contentHeight={50}
        eventShortHeight={50}
        viewHeight={"auto"}
        initialView="resourceTimelineDay"
        resourceAreaWidth={isMobile() ? "150px" : "170px"}
        slotMinWidth={25}
        dayMinWidth={5}
        dateIncrement={{ hours: 2 }}
        plugins={[
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin,
          resourceTimelinePlugin,
        ]}
        events={
          tasks
            ?.filter((task) => task.medicine)
            ?.map((task) => {
              return {
                id: task.id,
                resourceId: task.medicine?.id,
                title: getName(task.patient) || "Sans nom",
                start: moment(task.start).format("YYYY-MM-DD HH:mm:ss"),
                end: moment(task.end).format("YYYY-MM-DD HH:mm:ss"),
              };
            }) as EventSourceInput
        } // Fix: Cast events to EventSourceInput
        resources={getResources()}
        resourceLabelContent={(arg) => {
          let title = arg.resource.title;
          let medicineId = arg.resource.id;

          return (
            <TreatmentResource
              medicineId={arg.resource?.id}
              remainingDays={getRemainingDays(medicineId)}
              title={title}
              onEditClick={async () => {
                await openEditDialog(medicineId);
              }}
            ></TreatmentResource>
          );
        }}
        resourceAreaHeaderContent={(date) => {
          return <div>Médicaments</div>;
        }}
        dayCellContent={(date) => {
          return <div>Day Cell</div>;
        }}
        weekNumberContent={(date) => {
          return <div>Week Number</div>;
        }}
        slotLabelContent={(arg) => {
          return `${moment(arg.date).format("HH")}h `;
        }}
        eventColor="transparent"
        dayCellClassNames={(arg) => {
          return "myCell";
        }}
        slotLaneClassNames={(arg) => {
          let date = moment(arg.date);
          if (date.hour() < 8 || date.hour() > 16) return "nightshift-lane";
          return "";
        }}
        titleFormat={{ year: "numeric", month: "long", day: "numeric" }}
        displayEventEnd={true}
        dayMaxEvents={true}
        nowIndicator={false}
        scrollTimeReset={false}
        slotEventOverlap={false}
        editable={true}
        selectable={true}
        selectMirror={true}
        eventResourceEditable={true}
        select={async ({ start, end }) => {
          console.log("Range start:", start);
          console.log("Range end:", end);
          // Your asynchronous logic here...
        }}
        eventChange={async (arg) => {
          let a = arg.event.getResources();
          let task = tasks?.find((task) => task.id == arg.event.id);
          if (!task) return;
          task.start = moment(arg.event.start).format("YYYY-MM-DD HH:mm");
          task.end = moment(arg.event.end).format("YYYY-MM-DD HH:mm");
          await apiFetch(
            "tasks",
            "addMany",
            { tasks: [task] },
            "Erreur lors de la mise à jour de la tâche"
          );
        }}
        eventContent={(arg) => {
          let task = tasks?.find((task) => task.id == arg.event.id);
          if (!task) return <div className="p10"></div>;

          return (
            <TreatmentEvent
              task={task}
              onClick={async () => {
                if (!task) return;
                if (isNurse(USER?.get())) {
                  if (task.status == "finished") task.status = "pending";
                  else {
                    task.status = "finished";
                    task.finishedDate = moment().format("YYYY-MM-DD HH:mm:ss");
                  }
                  await apiFetch(
                    "tasks",
                    "addMany",
                    { tasks: [task] },
                    "Erreur lors de la mise à jour de la tâche"
                  );
                  onChange();
                  return;
                }
                await openTaskActions({
                  task,
                  onChange: () => {
                    onChange();
                  },
                });
              }}
            />
          );
        }}
        dateClick={async (arg) => {
          let medicineId = arg.resource?.id;
          if (!medicineId) return;
          let anyTask = tasks?.find(
            (task) => getItemId(task.medicine) == medicineId
          );
          if (!anyTask) return;
          let start = moment(arg.date).format("YYYY-MM-DD HH:00");
          await onDateClick(anyTask, start);
        }}
      />
    </div>
  );
}
